import { money, num, date, shortDate } from '@/lib/format';
import { EPS, LABELS } from './status';
import type { CashRecord, InvoiceRecord, ReceiptFile, Scope, StatementCashRecord, StatementInvoiceRecord } from './types';
import { Table, type Col } from './ui';
import s from './RecordList.module.css';

export type RecordKind = 'cash' | 'invoice';

/** 收付款 / 发票台账的统一视图（两个接口字段不同，Decimal 还可能是字符串） */
export type RecordVM = {
  id: string;
  occurredAt: string;
  amount?: number;
  type?: string;
  partnerName?: string;
  partnerCode?: string | null;
  settlementMonth?: string | null;
  note?: string | null;
  receiptFiles?: ReceiptFile[] | null;
  createdBy?: string;
  method?: string | null;
  referenceNo?: string | null;
  invoiceType?: string | null;
  invoiceNo?: string | null;
  invoiceTitle?: string | null;
  taxAmount?: number | null;
  taxRate?: number | null;
  /** 只有对账预览接口给出核销（分摊）金额 */
  allocated?: number;
  unallocated?: number;
};

type Alloc = Map<string, { allocatedAmount?: number; unallocatedAmount?: number }>;

export function toVM(r: CashRecord | InvoiceRecord, alloc?: Alloc): RecordVM {
  const a = alloc?.get(r.id);
  const partner = r.customer || r.supplier;
  const inv = r as InvoiceRecord;
  const cash = r as CashRecord;
  return {
    id: r.id,
    occurredAt: r.occurredAt,
    amount: r.amount === undefined ? undefined : num(r.amount) * (r.type === 'CUSTOMER_REFUND' ? -1 : 1),
    type: r.type,
    partnerName: partner?.name,
    partnerCode: r.supplier?.code,
    settlementMonth: r.settlementMonth,
    note: r.note,
    receiptFiles: r.receiptFiles,
    createdBy: r.createdBy?.name,
    method: cash.method,
    referenceNo: cash.referenceNo,
    invoiceType: inv.invoiceType,
    invoiceNo: inv.invoiceNo,
    invoiceTitle: inv.invoiceTitle,
    taxAmount: inv.taxAmount == null ? inv.taxAmount : num(inv.taxAmount),
    taxRate: inv.taxRate == null ? inv.taxRate : num(inv.taxRate),
    allocated: a?.allocatedAmount,
    unallocated: a?.unallocatedAmount,
  };
}

export function allocMap(rows?: (StatementCashRecord | StatementInvoiceRecord)[]): Alloc | undefined {
  if (!rows) return undefined;
  return new Map(rows.map((r) => [r.id, { allocatedAmount: r.allocatedAmount, unallocatedAmount: r.unallocatedAmount }]));
}

export function sumAmount(rows: RecordVM[]) {
  return rows.reduce((a, r) => a + num(r.amount), 0);
}

function Card({ r, kind, showPartner, scope }: { r: RecordVM; kind: RecordKind; showPartner: boolean; scope: Scope }) {
  const L = LABELS[scope];
  const title = kind === 'cash' ? r.type === 'CUSTOMER_REFUND' ? `客户退款 · ${r.method || '未填写方式'}` : r.method || `未填写${L.cashVerb}方式` : r.invoiceNo ? `发票 ${r.invoiceNo}` : '未填写发票号';
  const meta = kind === 'cash'
    ? [r.referenceNo && `流水 ${r.referenceNo}`, r.note].filter(Boolean)
    : [r.invoiceType === 'VAT_SPECIAL' ? '增值税专用发票' : r.invoiceType === 'VAT_GENERAL' ? '增值税普通发票' : '', r.invoiceTitle && `抬头 ${r.invoiceTitle}`, r.taxAmount != null && `税额 ${money(r.taxAmount)}${r.taxRate != null ? `（${r.taxRate}%）` : ''}`, r.note].filter(Boolean);
  return (
    <li className={s.card}>
      <div className={s.h}>
        <b><span className="tnum">{shortDate(r.occurredAt)}</span> · {title}</b>
        {r.amount !== undefined && <strong className="tnum">{money(r.amount)}</strong>}
      </div>
      {showPartner && r.partnerName && <div className={s.p}>{r.partnerName}{r.partnerCode ? <small> {r.partnerCode}</small> : null}</div>}
      {meta.length > 0 && <p className={s.m}>{meta.join(' · ')}</p>}
      {r.receiptFiles?.length ? <div className={s.m}>凭证：{r.receiptFiles.map((file, index) => <a key={`${file.fileUrl}-${index}`} href={file.fileUrl} target="_blank" rel="noopener noreferrer">{file.fileName}</a>)}</div> : null}
      <div className={s.f}>
        <span>{r.createdBy ? `${r.createdBy} 登记` : '—'}{r.settlementMonth ? ` · 归属 ${r.settlementMonth}` : ''}</span>
        {r.allocated !== undefined && (
          <span className="tnum">
            {kind === 'cash' ? '已核销' : '已核票'} {money(r.allocated)}
            {num(r.unallocated) > EPS && <em className={s.open}> · 未{kind === 'cash' ? '核销' : '核票'} {money(r.unallocated)}</em>}
          </span>
        )}
      </div>
    </li>
  );
}

function cols(kind: RecordKind, scope: Scope, showPartner: boolean, rows: RecordVM[], compact: boolean): Col<RecordVM>[] {
  const L = LABELS[scope];
  const amounts = rows.length === 0 || rows.some((r) => r.amount !== undefined);
  const alloc = rows.some((r) => r.allocated !== undefined);
  if (compact && kind === 'cash') {
    return [
      { key: 'd', title: '收款日期', w: 105, fix: true, render: (r) => <span className="tnum">{shortDate(r.occurredAt)}</span> },
      { key: 'm', title: '方式', w: 118, render: (r) => r.type === 'CUSTOMER_REFUND' ? `退款 · ${r.method || '—'}` : r.method || <span className="em">—</span> },
      { key: 'a', title: '金额', w: 128, align: 'r', render: (r) => (r.amount === undefined ? '—' : money(r.amount)) },
      { key: 'al', title: '已核销', w: 120, align: 'r', render: (r) => (r.allocated === undefined ? '—' : money(r.allocated)) },
      { key: 'un', title: '未核销', w: 120, align: 'r', render: (r) => (r.unallocated === undefined ? '—' : <span className={num(r.unallocated) > EPS ? s.open : 'em'}>{money(r.unallocated)}</span>) },
      { key: 'n', title: '备注', w: 180, render: (r) => r.note || <span className="em">—</span> },
    ];
  }
  const out: (Col<RecordVM> | false)[] = [
    { key: 'd', title: '发生日期', w: 108, fix: true, render: (r) => <span className="tnum">{date(r.occurredAt)}</span> },
    showPartner && { key: 'p', title: L.partner, w: 170, render: (r) => r.partnerName || <span className="em">—</span> },
    amounts && { key: 'a', title: kind === 'cash' ? `${L.cashVerb}金额` : `${L.invVerb}金额`, w: 128, align: 'r', render: (r) => (r.amount === undefined ? '—' : money(r.amount)) },
    alloc && { key: 'al', title: kind === 'cash' ? '已核销' : '已核票', w: 120, align: 'r', render: (r) => (r.allocated === undefined ? '—' : money(r.allocated)) },
    alloc && { key: 'un', title: kind === 'cash' ? '未核销' : '未核票', w: 120, align: 'r', render: (r) => (r.unallocated === undefined ? '—' : <span className={num(r.unallocated) > EPS ? s.open : 'em'}>{money(r.unallocated)}</span>) },
    kind === 'cash' && { key: 'm', title: '方式', w: 100, render: (r) => r.type === 'CUSTOMER_REFUND' ? `退款 · ${r.method || '—'}` : r.method || <span className="em">—</span> },
    kind === 'cash' && { key: 'ref', title: '流水 / 单号', w: 170, render: (r) => r.referenceNo || <span className="em">—</span> },
    kind === 'invoice' && { key: 'invoiceType', title: '发票类型', w: 140, render: (r) => r.invoiceType === 'VAT_SPECIAL' ? '增值税专用发票' : r.invoiceType === 'VAT_GENERAL' ? '增值税普通发票' : <span className="em">—</span> },
    kind === 'invoice' && { key: 'no', title: '发票号', w: 190, render: (r) => r.invoiceNo || <span className="em">—</span> },
    kind === 'invoice' && { key: 't', title: '抬头', w: 190, render: (r) => r.invoiceTitle || <span className="em">—</span> },
    kind === 'invoice' && amounts && { key: 'tax', title: '税额', w: 110, align: 'r', render: (r) => (r.taxAmount == null ? <span className="em">—</span> : money(r.taxAmount)) },
    { key: 'sm', title: '归属月份', w: 92, render: (r) => r.settlementMonth || <span className="em">—</span> },
    { key: 'n', title: '备注', w: 220, render: (r) => r.note || <span className="em">—</span> },
    { key: 'files', title: '凭证', w: 180, render: (r) => r.receiptFiles?.length ? r.receiptFiles.map((file, index) => <a key={`${file.fileUrl}-${index}`} href={file.fileUrl} target="_blank" rel="noopener noreferrer">{file.fileName}{index < (r.receiptFiles?.length || 0) - 1 ? '、' : ''}</a>) : <span className="em">—</span> },
    { key: 'u', title: '登记人', w: 84, render: (r) => r.createdBy || <span className="em">—</span> },
  ];
  return out.filter(Boolean) as Col<RecordVM>[];
}

/**
 * 台账列表。mode=auto 时按容器宽度自己切：窄是卡片，宽是表格（同一份数据渲染两份，由容器查询显隐，条数最多 200）。
 */
export function RecordList({ kind, scope, rows, showPartner = true, mode = 'auto', empty, boxed }: {
  kind: RecordKind;
  /** 列表页上每条一张独立卡片；面板里默认是发丝线分隔的行 */
  boxed?: boolean;
  scope: Scope;
  rows: RecordVM[];
  showPartner?: boolean;
  mode?: 'auto' | 'cards' | 'table' | 'compact';
  empty?: string;
}) {
  if (!rows.length) return <p className={s.none}>{empty || '这段时间没有记录。'}</p>;
  const cards = (
    <ul className={`${s.cards} ${boxed ? s.boxed : ''}`}>
      {rows.map((r) => <Card key={r.id} r={r} kind={kind} scope={scope} showPartner={showPartner} />)}
    </ul>
  );
  const table = <Table cols={cols(kind, scope, showPartner, rows, mode === 'compact')} rows={rows} rowKey={(r) => r.id} />;
  if (mode === 'cards') return cards;
  if (mode === 'compact') return <div className={s.compactTable}>{table}</div>;
  if (mode === 'table') return table;
  return (
    <div className={s.auto}>
      <div className={s.narrow}>{cards}</div>
      <div className={s.wide}>{table}</div>
    </div>
  );
}
